/** @file Registers a binding table with the user's overrides applied, and re-registers when they change. */
import { applyOverrides } from '@pose2d/shortcuts/applyOverrides';
import { detectPlatform } from '@pose2d/shortcuts/detectPlatform';
import { registerBindings } from '@pose2d/shortcuts/registerBindings';
import type { Binding, Platform } from '@pose2d/shortcuts/types';
import { settingsStore } from '../../stores/settingsStore';

/**
 * Use instead of registerBindings for every editor table (shell, panels, tools, dialogs): settings.ui.shortcuts
 * overrides apply (broken ones are dropped, so stored settings never throw) and a change re-registers the table.
 * Throws like registerBindings for a missing handler, before it subscribes.
 */
export function registerEffective(context: string, table: Binding[], handlers: Record<string, () => void>, platform: Platform = detectPlatform(globalThis.navigator)): () => void {
  const register = (overrides: Binding[]): (() => void) => registerBindings(context, applyOverrides(table, overrides, platform).table, handlers);
  let unregister = register(settingsStore.getState().shortcuts);
  const unsubscribe = settingsStore.subscribe((next, prev) => {
    if (next.shortcuts === prev.shortcuts) return;
    unregister();
    unregister = register(next.shortcuts);
  });
  return () => {
    unsubscribe();
    unregister();
  };
}
